import React, { useState, useEffect } from "react";
import { ArrowDown, ArrowUp, Phone, PhoneDisconnect, Clock, ChartBar, Target, Queue, ArrowsLeftRight, UserCircle } from "@phosphor-icons/react";
import API from "../services/api";
import "./Dashboard.css";

const fmt = (s) => { if (!s || s == 0) return "—"; const m = Math.floor(s/60); return m > 0 ? `${m}m ${s%60}s` : `${s}s`; };
const pct = (a, b) => b > 0 ? Math.round((a/b)*100) : 0;

const StatCard = ({ label, value, sub, color, icon }) => (
  <div className="stat-card">
    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
      {icon && <span style={{color:color||"var(--color-gray)",opacity:0.8}}>{icon}</span>}
      <span className="stat-label">{label}</span>
    </div>
    <div className="stat-value" style={{color:color||"var(--color-white)"}}>{value ?? "—"}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [queues, setQueues] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  const user = JSON.parse(localStorage.getItem("lv_user") || "{}");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const load = async () => {
      try {
        const [s, q, c, l] = await Promise.all([
          API.get("/calls/stats/summary"),
          API.get("/twilio/queues"),
          API.get("/twilio/calls"),
          API.get("/contacts?type=lead"),
        ]);
        setStats(s.data); setQueues(q.data); setActiveCalls(c.data); setLeads(l.data);
      } catch {}
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 10000);
    const clockInterval = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(interval); clearInterval(clockInterval); };
  }, []);

  if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%"}}><span className="spinner"/></div>;

  const answerRate = pct(parseInt(stats?.today_inbound_answered), parseInt(stats?.today_inbound));
  const transferRate = pct(parseInt(stats?.today_transfers_answered), parseInt(stats?.today_transfers));
  const queueAbandonRate = pct(parseInt(stats?.queue?.abandoned), parseInt(stats?.queue?.total_queued));
  const wonLeads = leads.filter(l => l.status === "won").length;
  const hotLeads = leads.filter(l => l.score >= 4).length;
  const followUps = leads.filter(l => l.follow_up_date && new Date(l.follow_up_date) <= new Date(Date.now() + 86400000)).length;
  const pipeline = leads.reduce((sum, l) => sum + (parseFloat(l.lead_value) || 0), 0);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{now.toLocaleDateString([],{weekday:"long",month:"long",day:"numeric"})} · {now.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit",second:"2-digit"})}</p>
        </div>
        {activeCalls.length > 0 && (
          <span style={{display:"flex",alignItems:"center",gap:6,background:"rgba(58,187,144,0.1)",border:"1px solid rgba(58,187,144,0.3)",borderRadius:20,padding:"5px 12px",fontSize:12,color:"var(--color-teal)",fontWeight:600}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:"var(--color-teal)",animation:"pulse 1.5s infinite"}}/>
            {activeCalls.length} live call{activeCalls.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="dashboard-body">

        {/* INBOUND CALLS */}
        <div className="section-label">Inbound Calls — Today</div>
        <div className="stats-grid" style={{gridTemplateColumns:"repeat(6,1fr)"}}>
          <StatCard label="Total Inbound" value={stats?.today_inbound||0} icon={<Phone size={15}/>} color="var(--color-white)"/>
          <StatCard label="Answered" value={stats?.today_inbound_answered||0} color="var(--color-teal)" icon={<Phone size={15} weight="fill"/>} sub={`${answerRate}% answer rate`}/>
          <StatCard label="Missed" value={stats?.today_missed||0} color="var(--color-danger)" icon={<PhoneDisconnect size={15}/>}/>
          <StatCard label="Avg Answer Speed" value={fmt(stats?.today_asa)} color="var(--color-blue)" icon={<Clock size={15}/>} sub="ASA"/>
          <StatCard label="Avg Talk Time" value={fmt(stats?.today_avg_talk)} color="var(--color-purple)" icon={<Clock size={15}/>}/>
          <StatCard label="Avg Hold Time" value={fmt(stats?.today_avg_hold)} color="var(--color-orange)" icon={<Clock size={15}/>}/>
        </div>

        {/* OUTBOUND + TRANSFERS + QUEUE */}
        <div className="stats-grid" style={{gridTemplateColumns:"repeat(6,1fr)"}}>
          <StatCard label="Outbound" value={stats?.today_outbound||0} icon={<ArrowUp size={15}/>} color="var(--color-white)"/>
          <StatCard label="Avg Duration" value={fmt(stats?.avg_duration)} color="var(--color-purple)" icon={<Clock size={15}/>} sub="7-day avg"/>
          <StatCard label="Direct Transfers" value={stats?.today_transfers||0} color="var(--color-blue)" icon={<ArrowsLeftRight size={15}/>} sub={`${transferRate}% success`}/>
          {isAdmin && <StatCard label="Queued Today" value={stats?.queue?.total_queued||0} color="var(--color-white)" icon={<Queue size={15}/>}/>}
          {isAdmin && <StatCard label="Queue Abandoned" value={stats?.queue?.abandoned||0} color="var(--color-danger)" icon={<PhoneDisconnect size={15}/>} sub={`${queueAbandonRate}% abandon rate`}/>}
          {isAdmin && <StatCard label="Avg Wait Time" value={fmt(stats?.queue?.avg_wait)} color="var(--color-orange)" icon={<Clock size={15}/>}/>}
          {!isAdmin && <StatCard label="This Week" value={stats?.week_total||0} color="var(--color-blue)" icon={<ChartBar size={15}/>} sub={`${stats?.week_answered||0} answered`}/>}
          {!isAdmin && <StatCard label="In Queue" value={queues[0]?.currentSize||0} color={queues[0]?.currentSize > 0 ? "var(--color-orange)" : "var(--color-white)"} icon={<Target size={15}/>}/>}
        </div>

        {/* MIDDLE ROW */}
        <div className="dashboard-grid" style={{gridTemplateColumns: isAdmin ? "1fr 1fr" : "1fr 1fr 1fr"}}>
          {/* Active Calls */}
          <div className="card">
            <div className="card-header">
              <h3>Active Calls</h3>
              <span className="badge badge-teal">{activeCalls.length} live</span>
            </div>
            {activeCalls.length === 0
              ? <div className="empty-state" style={{padding:"20px 0"}}><p>No active calls.</p></div>
              : activeCalls.map(call => (
                <div key={call.sid} className="active-call-item">
                  <div className={`direction-badge ${call.direction}`}>
                    {call.direction === "inbound" ? <ArrowDown size={13}/> : <ArrowUp size={13}/>}
                  </div>
                  <div className="active-call-info">
                    <div className="active-call-number">{call.direction === "inbound" ? call.from : call.to}</div>
                    <div className="active-call-meta">{call.direction} · in-progress</div>
                  </div>
                  <span style={{width:8,height:8,borderRadius:"50%",background:"var(--color-teal)",flexShrink:0}}/>
                </div>
              ))
            }
          </div>

          {/* Sales Queue */}
          <div className="card">
            <div className="card-header"><h3>Sales Queue</h3></div>
            {queues.length === 0
              ? <div className="empty-state" style={{padding:"20px 0"}}><p>No queue data.</p></div>
              : queues.map(q => (
                <div key={q.sid}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                    <div>
                      <div style={{fontSize:36,fontWeight:700,color:q.currentSize>0?"var(--color-orange)":"var(--color-teal)"}}>{q.currentSize}</div>
                      <div style={{fontSize:12,color:"var(--color-gray)"}}>callers waiting</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:36,fontWeight:700,color:"var(--color-white)"}}>{q.averageWaitTime||0}s</div>
                      <div style={{fontSize:12,color:"var(--color-gray)"}}>avg wait time</div>
                    </div>
                  </div>
                  <div style={{height:4,background:"var(--color-surface)",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${Math.min(100,(q.currentSize/10)*100)}%`,background:q.currentSize>0?"var(--color-orange)":"var(--color-teal)",borderRadius:2,transition:"width 0.5s"}}/>
                  </div>
                  {isAdmin && stats?.queue?.total_queued > 0 && (
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:16}}>
                      {[
                        {label:"Answered",value:stats.queue.answered||0,color:"var(--color-teal)"},
                        {label:"Abandoned",value:stats.queue.abandoned||0,color:"var(--color-danger)"},
                        {label:"Max Wait",value:fmt(stats.queue.max_wait),color:"var(--color-orange)"},
                      ].map(({label,value,color})=>(
                        <div key={label} style={{background:"var(--color-surface)",borderRadius:6,padding:"8px",textAlign:"center"}}>
                          <div style={{fontSize:16,fontWeight:700,color}}>{value}</div>
                          <div style={{fontSize:10,color:"var(--color-gray)",marginTop:2}}>{label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            }
          </div>

          {/* Lead Pipeline - agents only */}
          {!isAdmin && (
            <div className="card">
              <div className="card-header"><h3>Lead Pipeline</h3></div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {[
                  {label:"Total Leads", value:leads.length, color:"var(--color-white)"},
                  {label:"Hot Leads", value:hotLeads, color:"var(--color-orange)"},
                  {label:"Won", value:wonLeads, color:"var(--color-teal)"},
                  {label:"Follow-ups Today", value:followUps, color:"var(--color-blue)"},
                  {label:"Pipeline Value", value:`$${pipeline.toLocaleString()}`, color:"var(--color-purple)"},
                ].map(({label,value,color})=>(
                  <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:"1px solid var(--color-surface)"}}>
                    <span style={{fontSize:12,color:"var(--color-gray)"}}>{label}</span>
                    <span style={{fontSize:14,fontWeight:700,color}}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* AGENT PERFORMANCE - admin only */}
        {isAdmin && stats?.agents?.length > 0 && (
          <div className="card">
            <div className="card-header"><h3>Agent Performance — Today</h3></div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:"2px solid var(--color-surface-mid)"}}>
                    {["Agent","Inbound","Answered","Missed","Answer Rate","Outbound","Transfers","ASA","Talk Time","Hold"].map(h=>(
                      <th key={h} style={{padding:"8px 12px",textAlign:"left",color:"var(--color-gray)",fontWeight:500,fontSize:11,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stats.agents.map(a=>(
                    <tr key={a.agent_identity} style={{borderBottom:"1px solid var(--color-surface)"}}>
                      <td style={{padding:"10px 12px"}}>
                        <div style={{fontWeight:600,color:"var(--color-white)"}}>{a.first_name} {a.last_name}</div>
                        <div style={{fontSize:11,color:"var(--color-gray)"}}>{a.agent_identity}</div>
                      </td>
                      <td style={{padding:"10px 12px",color:"var(--color-white)"}}>{a.inbound||0}</td>
                      <td style={{padding:"10px 12px",color:"var(--color-teal)",fontWeight:600}}>{a.answered||0}</td>
                      <td style={{padding:"10px 12px",color:"var(--color-danger)"}}>{a.missed||0}</td>
                      <td style={{padding:"10px 12px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{flex:1,height:4,background:"var(--color-surface-mid)",borderRadius:2,overflow:"hidden",minWidth:40}}>
                            <div style={{height:"100%",width:`${pct(parseInt(a.answered),parseInt(a.inbound))}%`,background:"var(--color-teal)",borderRadius:2}}/>
                          </div>
                          <span style={{fontSize:11,color:"var(--color-gray)",whiteSpace:"nowrap"}}>{pct(parseInt(a.answered),parseInt(a.inbound))}%</span>
                        </div>
                      </td>
                      <td style={{padding:"10px 12px",color:"var(--color-white)"}}>{a.outbound||0}</td>
                      <td style={{padding:"10px 12px",color:"var(--color-blue)"}}>{a.transfers||0}</td>
                      <td style={{padding:"10px 12px",color:"var(--color-blue)",whiteSpace:"nowrap"}}>{fmt(a.avg_asa)}</td>
                      <td style={{padding:"10px 12px",color:"var(--color-purple)",whiteSpace:"nowrap"}}>{fmt(a.avg_talk)}</td>
                      <td style={{padding:"10px 12px",color:"var(--color-orange)",whiteSpace:"nowrap"}}>{fmt(a.avg_hold)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* LEAD PIPELINE - admin */}
        {isAdmin && (
          <div className="dashboard-grid" style={{gridTemplateColumns:"1fr 1fr"}}>
            <div className="card">
              <div className="card-header"><h3>Lead Pipeline</h3></div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {[
                  {label:"Total Leads", value:leads.length, color:"var(--color-white)"},
                  {label:"Hot Leads", value:hotLeads, color:"var(--color-orange)"},
                  {label:"Won", value:wonLeads, color:"var(--color-teal)"},
                  {label:"Follow-ups Today", value:followUps, color:"var(--color-blue)"},
                  {label:"Pipeline Value", value:`$${pipeline.toLocaleString()}`, color:"var(--color-purple)"},
                ].map(({label,value,color})=>(
                  <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:"1px solid var(--color-surface)"}}>
                    <span style={{fontSize:12,color:"var(--color-gray)"}}>{label}</span>
                    <span style={{fontSize:14,fontWeight:700,color}}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <div className="card-header"><h3>Lead Status Breakdown</h3></div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {[
                  {label:"New",color:"#6b7280",value:"new"},
                  {label:"Contacted",color:"#3b82f6",value:"contacted"},
                  {label:"Qualified",color:"#8b5cf6",value:"qualified"},
                  {label:"Proposal",color:"#f59e0b",value:"proposal"},
                  {label:"Won",color:"#10b981",value:"won"},
                  {label:"Lost",color:"#ef4444",value:"lost"},
                ].map(s=>{
                  const count = leads.filter(l=>(l.status||"new")===s.value).length;
                  const p = pct(count,leads.length);
                  return (
                    <div key={s.value}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                        <span style={{color:s.color,fontWeight:500}}>{s.label}</span>
                        <span style={{color:"var(--color-gray)"}}>{count} ({p}%)</span>
                      </div>
                      <div style={{height:4,background:"var(--color-surface-mid)",borderRadius:2,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${p}%`,background:s.color,borderRadius:2}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* LEAD STATUS - agents */}
        {!isAdmin && (
          <div className="card">
            <div className="card-header"><h3>Lead Status Breakdown</h3></div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[
                {label:"New",color:"#6b7280",value:"new"},
                {label:"Contacted",color:"#3b82f6",value:"contacted"},
                {label:"Qualified",color:"#8b5cf6",value:"qualified"},
                {label:"Proposal",color:"#f59e0b",value:"proposal"},
                {label:"Won",color:"#10b981",value:"won"},
                {label:"Lost",color:"#ef4444",value:"lost"},
              ].map(s=>{
                const count = leads.filter(l=>(l.status||"new")===s.value).length;
                const p = pct(count,leads.length);
                return (
                  <div key={s.value} style={{flex:1,minWidth:80,background:"var(--color-surface)",borderRadius:8,padding:"12px",border:`1px solid ${s.color}33`}}>
                    <div style={{fontSize:11,color:s.color,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>{s.label}</div>
                    <div style={{fontSize:22,fontWeight:700,color:"var(--color-white)"}}>{count}</div>
                    <div style={{fontSize:11,color:"var(--color-gray)",marginTop:2}}>{p}%</div>
                    <div style={{height:3,background:"var(--color-surface-mid)",borderRadius:2,marginTop:8,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${p}%`,background:s.color,borderRadius:2}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
